import { z } from 'zod';
import { FilterOperators } from './table';
import { agentUpdateStateSchema } from './agent-auto-update';
// KIR-3035: endpoint (machine) identity + details surfaced across Inventory,
// Alerts, and Shadow-AI. An endpoint is a machine the Kirin agent runs on,
// keyed by the agent's persisted machine_id.
/** Full details for one endpoint, as shown in the inventory endpoints dialog. */
export const endpointDetailsSchema = z.object({
    /** The agent's persisted machine_id — the endpoint ID throughout the feature. */
    machineId: z.string(),
    /** Hostname. */
    name: z.string().nullable(),
    osString: z.string().nullable(),
    ipAddresses: z.array(z.string()),
    macAddresses: z.array(z.string()),
    /** Agent version as of the last endpoint report (the agent's periodic
     * endpoint reporter, ~hourly — not the keepalive/handshake path). */
    agentVersion: z.string().nullable(),
    lastSeen: z.string().datetime().nullable(),
    firstSeen: z.string().datetime().nullable(),
    /*
      KIR-5191: auto-update status. Optional because this schema also types the
      endpoint snapshots stored on alerts and agent-command events, which never
      carry it; the Inventory -> Users endpoints list always sends all four.
    */
    /** Auto-update state; null before the first update check. */
    updateState: agentUpdateStateSchema.nullable().optional(),
    lastUpdateCheckAt: z.string().datetime().nullable().optional(),
    lastUpdateError: z.string().nullable().optional(),
    /** The version the last started update was installing. */
    updateTargetVersion: z.string().nullable().optional(),
});
/** A user's endpoints, returned by the inventory endpoints endpoint. */
export const endpointListSchema = z.object({
    endpoints: z.array(endpointDetailsSchema),
    /*
      KIR-5191. Optional so a newer dashboard still parses an older backend's
      answer (deploy skew, rollback); absent reads as unknown / Off.
    */
    /** Knostic's latest agent version; null when unknown. */
    latestAgentVersion: z.string().nullable().optional(),
    /** The org's "Automatic agent updates" switch. */
    autoUpdateEnabled: z.boolean().optional(),
});
/** Payload the agent's periodic endpoint reporter POSTs to upsert the registry. */
export const reportEndpointSchema = z.object({
    machineId: z.string().min(1),
    name: z.string().nullable().optional(),
    osString: z.string().nullable().optional(),
    // `.nullable()` as well as `.optional()`: a client that serializes an empty
    // address list as JSON `null` (e.g. a Go nil slice without omitempty) must not
    // 400 the whole report — null is treated as "no addresses reported" (KIR-3035).
    ipAddresses: z.array(z.string()).nullable().optional(),
    macAddresses: z.array(z.string()).nullable().optional(),
    agentVersion: z.string().nullable().optional(),
});
// ============================================================================
// KIR-5098: endpoint name-or-ID filter, shared by every table that filters by
// endpoint so the operators and matching rules are the same everywhere.
// ============================================================================
/** Filter / search / sort field key used by every endpoint-filterable table. */
export const ENDPOINT_FILTER_FIELD = 'endpoint';
/**
 * Operators the endpoint filter accepts. CONTAINS = name or ID contains the
 * value; INCLUDES is the UI's "Equals" = exact name or exact ID. Both are
 * case-insensitive and ignore surrounding whitespace.
 */
export const ENDPOINT_FILTER_OPERATORS = [
    FilterOperators.CONTAINS,
    FilterOperators.INCLUDES,
];
/** Trim each value and drop blanks; matching is case-insensitive downstream. */
export function normalizeEndpointFilterValues(values) {
    return values.map(v => v.trim()).filter(v => v.length > 0);
}
/** One endpoint suggestion in a filter's "Matches" list. */
export const endpointFilterOptionKindSchema = z.enum(['id', 'host']);
/**
 * Per-row endpoint summary on lists grouped by tool (Browser / Installed AI
 * Tools). `endpointCount` = distinct machines; `matchedEndpoints` = up to 3
 * labels (hostname, else ID) matching the active endpoint filter, with
 * `matchedEndpointCount` the full number — both only while that filter is set.
 * Optional so payloads from a backend without KIR-5098 still parse.
 */
export const endpointRowSummaryShape = {
    endpointCount: z.number().int().min(0).optional(),
    matchedEndpoints: z.array(z.string()).optional(),
    matchedEndpointCount: z.number().int().min(0).optional(),
};
/** Most matched-endpoint labels a grouped row carries for its chip. */
export const MATCHED_ENDPOINTS_LIMIT = 3;
//# sourceMappingURL=endpoints.js.map